// Ensures Shopify's Customer Privacy API is loaded before we read consent.
// customerPrivacy is injected asynchronously and is often NOT ready when the
// banner's connectedCallback runs, so reading currentVisitorConsent() too early
// would miss stored consent and wrongly show the banner. We wait for it explicitly.
let _cpReadyPromise = null;

function _cookieLoadPrivacyApi() {
  if (_cpReadyPromise) return _cpReadyPromise;

  _cpReadyPromise = new Promise((resolve) => {
    // Already available (e.g. cached / late navigation).
    if (window.Shopify?.customerPrivacy?.shouldShowBanner) {
      resolve(window.Shopify.customerPrivacy);
      return;
    }

    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        () => resolve(window.Shopify?.customerPrivacy || null)
      );
    } else {
      // loadFeatures not present — no consent API on this store. Use localStorage fallback.
      resolve(null);
    }
  });

  return _cpReadyPromise;
}

function _cookieSetConsent(consent, callback) {
  if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
    window.Shopify.customerPrivacy.setTrackingConsent(consent, (response) => {
      // On the local `shopify theme dev` server this write fails (ERR_SSL_PROTOCOL_ERROR)
      // because it can't reach the storefront API — expected in local dev, works on a real domain.
      if (response?.error) console.warn('[cookie] consent not stored:', response.error);
      callback?.(response);
    });
  } else {
    try { localStorage.setItem('alto_cookie_consent', JSON.stringify(consent)); } catch {}
    callback?.();
  }
}

function _cookieGetCurrentConsent() {
  if (window.Shopify?.customerPrivacy?.currentVisitorConsent) {
    const c = window.Shopify.customerPrivacy.currentVisitorConsent();
    return {
      preferences: c.preferences === 'yes',
      marketing: c.marketing === 'yes',
      analytics: c.analytics === 'yes',
    };
  }
  try {
    return JSON.parse(localStorage.getItem('alto_cookie_consent') || '{}');
  } catch {
    return {};
  }
}

// Whether the visitor has already made a consent choice. currentVisitorConsent()
// returns '' for each category before a choice and 'yes'/'no' after — any non-empty
// value means the visitor decided.
function _cookieHasDecided(cp) {
  if (cp?.currentVisitorConsent) {
    const c = cp.currentVisitorConsent();
    return !!(c.marketing || c.analytics || c.preferences || c.sale_of_data);
  }
  return !!localStorage.getItem('alto_cookie_consent');
}

function _cookieShouldShow(cp) {
  // shouldShowBanner() is unreliable in this store's config — it keeps returning true
  // even after consent is recorded (and the _tracking_consent cookie it reads is never
  // written). currentVisitorConsent() persists the choice across navigation, so a
  // recorded consent is the authoritative "already decided" signal.
  if (_cookieHasDecided(cp)) return false;
  if (cp?.shouldShowBanner) return cp.shouldShowBanner();
  if (cp?.shouldShowGDPRBanner) return cp.shouldShowGDPRBanner();
  return true;
}

class CookieConsentBanner extends HTMLElement {
  connectedCallback() {
    if (window.Shopify?.designMode) return;

    // Decide visibility only after the privacy API has had a chance to load.
    _cookieLoadPrivacyApi().then((cp) => {
      if (_cookieShouldShow(cp)) this.removeAttribute('hidden');
    });

    this.querySelector('[data-cookie-accept-all]')?.addEventListener('click', () => this._acceptAll());
    this.querySelector('[data-cookie-preferences-btn]')?.addEventListener('click', () => {
      document.getElementById('CookieSettingsModal')?.open();
    });
  }

  _acceptAll() {
    _cookieSetConsent(
      { analytics: true, marketing: true, preferences: true, sale_of_data: false },
      () => {}
    );
    this.setAttribute('hidden', '');
  }
}

class CookieSettingsModal extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-cookie-close]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-cookie-accept-all]')?.addEventListener('click', () => this._acceptAll());
    this.querySelector('[data-cookie-save]')?.addEventListener('click', () => this._save());

    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hasAttribute('hidden')) {
        e.preventDefault();
        this.close();
      }
    });
  }

  open() {
    const current = _cookieGetCurrentConsent();
    this.querySelectorAll('[data-cookie-toggle]').forEach((toggle) => {
      toggle.checked = current[toggle.dataset.cookieToggle] ?? false;
    });

    this.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.querySelector('[data-cookie-close]')?.focus();
    });
  }

  close() {
    this.setAttribute('hidden', '');
    document.body.style.overflow = '';
    document.querySelector('[data-cookie-preferences-btn]')?.focus();
  }

  _acceptAll() {
    this.querySelectorAll('[data-cookie-toggle]:not(:disabled)').forEach((t) => (t.checked = true));
    this._save();
  }

  _save() {
    const consent = { sale_of_data: false };
    this.querySelectorAll('[data-cookie-toggle]').forEach((toggle) => {
      consent[toggle.dataset.cookieToggle] = toggle.checked;
    });

    _cookieSetConsent(consent, () => {});
    this.close();
    document.getElementById('CookieConsentBanner')?.setAttribute('hidden', '');
  }
}

customElements.define('cookie-consent-banner', CookieConsentBanner);
customElements.define('cookie-settings-modal', CookieSettingsModal);
